// src/components/PriorityAccessSection.jsx
import React, { useState } from 'react';
import {
  Megaphone,
  KeyRound,
  ArrowRight,
  CheckCircle2,
  ShieldCheck,
  Clock,
  BellRing
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_BASE || '/api';

export default function PriorityAccessSection() {
  const priorityOptions = [
    {
      title: 'Priority for Sale',
      description: 'Get exclusive first access to properties for sale.',
      color: '#B02A33',
      hoverBg: '#FEE2E2',
      href: '/buyers'
    },
    {
      title: 'Priority to Rent',
      description: 'Be the first to see our new rental listings.',
      color: '#1e3a8a',
      hoverBg: '#DBEAFE',
      href: '/tenants'
    },
  ];

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    postcode: '',
    sale: true,
    rent: true,
    consent: false,
  });
  const [status, setStatus] = useState({ state: 'idle', msg: '' });

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.consent || (!form.sale && !form.rent)) {
      setStatus({ state: 'error', msg: 'Please fill required fields and select at least one preference.' });
      return;
    }
    try {
      setStatus({ state: 'loading', msg: 'Submitting…' });
      const res = await fetch(`${API_BASE}/priority_access.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.ok === false) throw new Error(json.error || `HTTP ${res.status}`);
      setStatus({ state: 'success', msg: 'Thanks! You’re on the priority list.' });
      setForm({ name: '', email: '', phone: '', postcode: '', sale: true, rent: true, consent: false });
    } catch (err) {
      setStatus({ state: 'error', msg: err.message || 'Something went wrong. Please try again.' });
    }
  };

  return (
    <>
      <style>{`
        .priority-section {
          position: relative;
          isolation: isolate;
          overflow: hidden;
          background:
            radial-gradient(800px 400px at 10% -10%, #e2e8f0 0%, #f8fafc 60%, #f8fafc 100%);
          font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
          padding: clamp(3rem, 6vw, 6rem) 1rem;
        }
        .priority-container { max-width: 1200px; margin: 0 auto; }

        /* subtle blobs */
        .blob {
          position: absolute; inset: auto auto 10% -6%;
          width: min(380px, 80vw); height: min(380px, 80vw); border-radius: 50%;
          background: radial-gradient(circle at 30% 30%, #dbedff 0%, rgba(219,237,255,.0) 60%);
          filter: blur(30px); opacity: .5; z-index: -1;
        }
        .blob2 {
          position: absolute; inset: -12% -8% auto auto;
          width: min(420px, 85vw); height: min(420px, 85vw); border-radius: 50%;
          background: radial-gradient(circle at 65% 40%, #fee2e2 0%, rgba(254,226,226,.0) 60%);
          filter: blur(30px); opacity: .5; z-index: -1;
        }

        .priority-row {
          display: grid; gap: clamp(1.5rem, 3vw, 3rem);
          grid-template-columns: 1fr; align-items: start;
        }
        @media (min-width: 1024px) {
          .priority-row { grid-template-columns: 7fr 5fr; }
        }

        .eyebrow {
          display: inline-flex; align-items: center; gap: .5rem;
          color: #0f172a; font-weight: 800; letter-spacing: .12em; text-transform: uppercase;
          background: #fff; border: 1px solid #e5e7eb; border-radius: 999px;
          padding: .4rem .8rem; margin-bottom: .75rem;
        }
        .priority-icon { color: #f59e0b; }

        .priority-title {
          margin: 0; color: #0f172a;
          font-size: clamp(1.8rem, 3.5vw, 2.75rem); line-height: 1.1; font-weight: 900;
        }
        .priority-sub {
          margin: .75rem 0 1.25rem; color: #475569; font-size: 1.05rem; max-width: 46ch;
        }

        .benefits { display: grid; gap: .6rem; margin: 1rem 0 1.25rem; }
        .benefit { display: flex; align-items: center; gap: .6rem; color: #0f172a; }
        .benefit svg { color: #16a34a; }

        .trust-strip {
          display: flex; flex-wrap: wrap; gap: .75rem; align-items: center; margin-top: .5rem;
          color: #334155; font-size: .95rem;
        }
        .trust-chip {
          display: inline-flex; align-items: center; gap: .45rem;
          background: #fff; border: 1px solid #e2e8f0; padding: .4rem .7rem; border-radius: 10px;
        }

        .cards { display: flex; flex-direction: column; gap: 1rem; }
        .card {
          display: flex; align-items: center; justify-content: space-between;
          background: #ffffff; border: 1px solid #e2e8f0; border-radius: 12px;
          padding: 1.1rem 1.25rem; text-decoration: none; color: inherit;
          transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease;
        }
        .card:hover { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(2,6,23,.08); border-color:#cbd5e1; }
        .card-content { display:flex; align-items:center; gap: 1rem; }
        .card-icon {
          display:flex; align-items:center; justify-content:center;
          background: #f1f5f9; border-radius: 999px; width: 44px; height: 44px;
          transition: background .25s ease;
        }
        .card:hover .card-icon { background: var(--hover-bg); }
        .card-title { margin: 0; font-weight: 700; color: #0f172a; font-size: 1.04rem; }
        .card-desc { margin: .25rem 0 0; font-size: .92rem; color: #64748b; }
        .card-arrow { color: #cbd5e1; transition: transform .25s ease, color .25s; }
        .card:hover .card-arrow { transform: translateX(4px); color: #0f172a; }

        /* Form */
        .priority-form {
          background: #ffffff; border: 1px solid #e2e8f0; border-radius: 16px;
          padding: 1.25rem; margin-top: 1rem;
          box-shadow: 0 8px 24px rgba(15,23,42,.06);
        }
        .form-grid { display:grid; gap: .9rem; grid-template-columns: 1fr; }
        @media (min-width: 640px) { .form-grid { grid-template-columns: 1fr 1fr; } }
        .field { display:flex; flex-direction:column; gap: .35rem; }
        .label { font-size: .88rem; color:#334155; font-weight:600; }
        .input {
          border: 1px solid #cbd5e1; border-radius: 10px; padding: .7rem .85rem; font-size: .95rem;
          outline: none; transition: border-color .2s, box-shadow .2s;
        }
        .input:focus { border-color: #1e3a8a; box-shadow: 0 0 0 3px rgba(30,58,138,.15); }

        .checks { display:flex; gap: 1rem; flex-wrap: wrap; align-items:center; }
        .check { display:flex; align-items:center; gap: .5rem; }
        .consent { display:flex; align-items:flex-start; gap: .6rem; font-size: .9rem; color:#475569; margin-top:.4rem; }

        .actions { display:flex; align-items:center; gap: .75rem; flex-wrap: wrap; margin-top: .4rem; }
        .btn {
          appearance: none; border: none; cursor: pointer;
          background: #1e3a8a; color: #fff; font-weight: 800;
          border-radius: 999px; padding: .85rem 1.2rem; font-size: .95rem;
          transition: transform .15s ease, box-shadow .2s ease, background .2s ease;
        }
        .btn:hover { transform: translateY(-1px); box-shadow: 0 10px 24px rgba(30,58,138,.25); background:#1b3480; }
        .btn:disabled { opacity:.7; cursor:not-allowed; box-shadow:none; transform:none; }

        .note { color:#64748b; font-size:.85rem; }
        .status { margin-top:.75rem; font-size:.92rem; }
        .status.ok { color:#166534; }
        .status.err { color:#991b1b; }

        /* small helper badges */
        .badges { display:flex; gap:.5rem; flex-wrap:wrap; margin:.75rem 0 0; }
        .badge {
          display:inline-flex; align-items:center; gap:.45rem;
          background:#f8fafc; border:1px solid #e2e8f0; color:#0f172a;
          padding:.35rem .6rem; border-radius:999px; font-size:.8rem; font-weight:700;
        }
      `}</style>

      <section className="priority-section" id="property-alerts">
        <div className="blob" />
        <div className="blob2" />
        <div className="priority-container">
          <div className="priority-row">
            {/* LEFT: pitch + benefits + trust */}
            <div>
              <div className="eyebrow">
                <Megaphone className="priority-icon" size={18} />
                Priority Access
              </div>
              <h2 className="priority-title">
                Property alerts tailored to your search
              </h2>
              <p className="priority-sub">
                Receive useful email updates when matching homes are added to our website.
                Beat the rush, book earlier viewings, and never miss your ideal property.
              </p>

              <div className="benefits">
                <div className="benefit"><CheckCircle2 size={18} /> Updates for matching properties</div>
                <div className="benefit"><CheckCircle2 size={18} /> Tailored alerts for price, beds & postcode</div>
                <div className="benefit"><CheckCircle2 size={18} /> One-tap viewing requests</div>
              </div>

              <div className="trust-strip">
                <span className="trust-chip"><ShieldCheck size={16}/> Free to join</span>
                <span className="trust-chip"><Clock size={16}/> Flexible preferences</span>
                <span className="trust-chip"><BellRing size={16}/> Unsubscribe anytime</span>
              </div>

              {/* quick option links */}
              <div className="badges">
                <a className="badge" href="/buyers" aria-label="Priority for sale">For Buyers</a>
                <a className="badge" href="/tenants" aria-label="Priority to rent">For Tenants</a>
              </div>
            </div>

            {/* RIGHT: cards + form */}
            <div>
              {/* cards */}
              <div className="cards">
                {priorityOptions.map((opt) => (
                  <a key={opt.title} href={opt.href} className="card" style={{ '--hover-bg': opt.hoverBg }}>
                    <div className="card-content">
                      <div className="card-icon" style={{ color: opt.color }}>
                        <KeyRound size={22} />
                      </div>
                      <div>
                        <h3 className="card-title">{opt.title}</h3>
                        <p className="card-desc">{opt.description}</p>
                      </div>
                    </div>
                    <ArrowRight className="card-arrow" />
                  </a>
                ))}
              </div>

              {/* signup form */}
              <form className="priority-form" onSubmit={onSubmit} noValidate>
                <div className="form-grid">
                  <div className="field">
                    <label className="label" htmlFor="name">Full name *</label>
                    <input id="name" name="name" className="input" value={form.name} onChange={onChange} required />
                  </div>
                  <div className="field">
                    <label className="label" htmlFor="email">Email *</label>
                    <input id="email" name="email" type="email" className="input" value={form.email} onChange={onChange} required />
                  </div>
                  <div className="field">
                    <label className="label" htmlFor="phone">Phone</label>
                    <input id="phone" name="phone" className="input" value={form.phone} onChange={onChange} />
                  </div>
                  <div className="field">
                    <label className="label" htmlFor="postcode">Postcode</label>
                    <input id="postcode" name="postcode" className="input" value={form.postcode} onChange={onChange} />
                  </div>
                </div>

                <div className="checks" style={{ marginTop: '.5rem' }}>
                  <label className="check"><input type="checkbox" name="sale" checked={form.sale} onChange={onChange} /> For sale alerts</label>
                  <label className="check"><input type="checkbox" name="rent" checked={form.rent} onChange={onChange} /> To rent alerts</label>
                </div>

                <label className="consent">
                  <input type="checkbox" name="consent" checked={form.consent} onChange={onChange} />
                  <span>
                    I agree to receive priority alerts from MRM Lettings. I understand I can unsubscribe anytime.
                  </span>
                </label>

                <div className="actions">
                  <button className="btn" type="submit" disabled={status.state === 'loading'}>
                    {status.state === 'loading' ? 'Submitting…' : 'Join Priority Access'}
                  </button>
                  <span className="note">It’s free and only takes a moment.</span>
                </div>

                {status.state === 'success' && <div className="status ok">✅ {status.msg}</div>}
                {status.state === 'error' && <div className="status err">⚠️ {status.msg}</div>}
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
